import { KeyboardEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton, TextArea } from "@carbon/react";
import { Send } from "@carbon/icons-react";

export const MAX_QUESTION_LENGTH = 4000;

type Props = {
  disabled: boolean;
  /** `onSent` clears the input; a question that fails to send stays where it was typed. */
  onSend: (text: string, onSent: () => void) => void;
};

/** Question input: Enter sends, Shift+Enter adds a line. */
const ScreenAssistantComposer = ({ disabled, onSend }: Props) => {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const question = text.trim();
  const canSend = !disabled && question.length > 0;

  const submit = () => {
    if (canSend) onSend(question, () => setText(""));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <form
      className="screen-assistant__composer"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <TextArea
        id="screen-assistant-input"
        labelText={t("screenAssistant.inputLabel")}
        hideLabel
        placeholder={t("screenAssistant.inputPlaceholder")}
        rows={2}
        maxLength={MAX_QUESTION_LENGTH}
        value={text}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={onKeyDown}
      />
      <IconButton
        type="submit"
        kind="primary"
        size="lg"
        label={t("screenAssistant.send")}
        disabled={!canSend}
        className="screen-assistant__send"
      >
        <Send />
      </IconButton>
    </form>
  );
};

export default ScreenAssistantComposer;
